import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * URL parametresi için serialize/deserialize tanımı.
 *
 * @template T - Parametrenin runtime tipi (string, number, boolean, string[] vb.)
 *
 * @example
 * ```ts
 * const categoryDef: UrlParamDefinition<string[]> = {
 *   defaultValue: [],
 *   serialize: (v) => v.join(','),
 *   deserialize: (v) => v ? v.split(',') : [],
 * };
 * ```
 */
export interface UrlParamDefinition<T = any> {
  /** URL'de parametre yoksa kullanılacak varsayılan değer. */
  defaultValue: T;

  /**
   * Runtime değerini URL string'ine çevirir.
   * Döndürülen değer `null` veya boş string ise parametre URL'den kaldırılır.
   *
   * @param value - Runtime değeri
   * @returns URL'de saklanacak string veya null (parametreyi kaldırmak için)
   */
  serialize: (value: T) => string | null;

  /**
   * URL string'ini runtime değerine çevirir.
   *
   * @param raw - URL'deki ham string değer (parametre yoksa `null`)
   * @returns Runtime değeri
   */
  deserialize: (raw: string | null) => T;
}

/**
 * `useUrlSearchParams` hook'unun yapılandırma seçenekleri.
 *
 * @template TSchema - Parametre şeması tipi (`Record<string, UrlParamDefinition>`)
 */
export interface UseUrlSearchParamsOptions<TSchema extends Record<string, UrlParamDefinition>> {
  /**
   * URL parametre tanımları.
   * Her key bir URL search parametresine karşılık gelir.
   *
   * @example
   * ```ts
   * schema: {
   *   page:  { defaultValue: 1,  serialize: String, deserialize: (r) => Number(r) || 1 },
   *   sort:  { defaultValue: '', serialize: (v) => v || null, deserialize: (r) => r ?? '' },
   * }
   * ```
   */
  schema: TSchema;

  /**
   * Browser back/forward navigasyonunda (POP) çağrılır.
   * Programatik URL değişikliklerinde (`setParam`, `setParams`) çağrılmaz.
   * Component'in veriyi yeniden fetch etmesi için kullanılır.
   *
   * @param params - POP sonrası URL'den parse edilmiş tüm parametre değerleri
   */
  onPopState?: (params: InferUrlParams<TSchema>) => void;

  /**
   * `true` ise ilk render'da URL'de eksik parametreler varsa
   * `history.replace` ile default değerler URL'e yazılır.
   * Varsayılan: `false`
   */
  writeDefaultsToUrl?: boolean;
}

/**
 * Schema tipinden runtime parametre tiplerini çıkarır.
 *
 * @example
 * ```ts
 * type Schema = {
 *   page: UrlParamDefinition<number>;
 *   sort: UrlParamDefinition<string>;
 * };
 * type Params = InferUrlParams<Schema>;
 * // { page: number; sort: string }
 * ```
 */
export type InferUrlParams<TSchema extends Record<string, UrlParamDefinition>> = {
  [K in keyof TSchema]: TSchema[K] extends UrlParamDefinition<infer V> ? V : never;
};

/**
 * `useUrlSearchParams` hook'unun dönüş tipi.
 *
 * @template TSchema - Parametre şeması tipi
 */
export interface UseUrlSearchParamsReturn<TSchema extends Record<string, UrlParamDefinition>> {
  /** URL'den parse edilmiş mevcut parametre değerleri. */
  params: InferUrlParams<TSchema>;

  /**
   * Tek bir parametreyi günceller ve URL'e yansıtır.
   *
   * @param key - Güncellenecek parametre adı
   * @param value - Yeni değer
   */
  setParam: <K extends keyof TSchema & string>(key: K, value: TSchema[K] extends UrlParamDefinition<infer V> ? V : never) => void;

  /**
   * Birden fazla parametreyi tek seferde günceller ve URL'e yansıtır.
   * Verilmeyen parametreler mevcut değerlerini korur.
   *
   * @param partial - Güncellenecek parametre key-value çiftleri
   */
  setParams: (partial: Partial<InferUrlParams<TSchema>>) => void;

  /** Tüm parametreleri varsayılan değerlere sıfırlar ve URL'i günceller. */
  resetParams: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Verilen schema ve search string'den parametre değerlerini parse eder.
 * URL'de bulunmayan parametreler için schema'daki defaultValue kullanılır.
 */
function parseParams<TSchema extends Record<string, UrlParamDefinition>>(schema: TSchema, search: string): InferUrlParams<TSchema> {
  const searchParams = new URLSearchParams(search);
  const result = {} as Record<string, any>;

  for (const key of Object.keys(schema)) {
    const def = schema[key];
    const raw = searchParams.get(key);
    result[key] = def.deserialize(raw);
  }

  return result as InferUrlParams<TSchema>;
}

/**
 * Parametre değerlerini serialize ederek URLSearchParams oluşturur.
 * `null` veya boş string dönen parametreler URL'den çıkarılır.
 * Schema dışındaki mevcut URL parametreleri korunur.
 */
function buildSearchString<TSchema extends Record<string, UrlParamDefinition>>(schema: TSchema, values: InferUrlParams<TSchema>, currentSearch: string): string {
  const searchParams = new URLSearchParams(currentSearch);

  for (const key of Object.keys(schema)) {
    const def = schema[key];
    const serialized = def.serialize((values as any)[key]);

    if (serialized === null || serialized === '') {
      searchParams.delete(key);
    } else {
      searchParams.set(key, serialized);
    }
  }

  return searchParams.toString();
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * URL search parametrelerini reactive state olarak yöneten generic hook.
 *
 * Herhangi bir key-value çiftini URL ile senkronize eder.
 * Browser back/forward butonlarıyla uyumlu çalışır — POP navigasyonunda
 * `onPopState` callback'i çağrılarak component'in veriyi yeniden fetch etmesi sağlanır.
 *
 * Schema-based serialize/deserialize desteği sayesinde string, number, boolean,
 * array gibi farklı tipleri URL'de saklayabilir.
 *
 * @template TSchema - Parametre şeması tipi
 *
 * @example
 * ```tsx
 * const { params, setParam, setParams } = useUrlSearchParams({
 *   schema: {
 *     page:     { defaultValue: 1,  serialize: String, deserialize: (r) => Number(r) || 1 },
 *     limit:    { defaultValue: 10, serialize: String, deserialize: (r) => Number(r) || 10 },
 *     search:   { defaultValue: '', serialize: (v) => v || null, deserialize: (r) => r ?? '' },
 *     category: {
 *       defaultValue: [] as string[],
 *       serialize: (v) => v.length ? v.join(',') : null,
 *       deserialize: (r) => r ? r.split(',') : [],
 *     },
 *   },
 *   onPopState: (params) => {
 *     dispatch(fetchData(params));
 *   },
 * });
 *
 * // Tek param güncelleme
 * setParam('page', 2);
 *
 * // Birden fazla param güncelleme
 * setParams({ search: 'laptop', page: 1 });
 * ```
 */
export function useUrlSearchParams<TSchema extends Record<string, UrlParamDefinition>>(options: UseUrlSearchParamsOptions<TSchema>): UseUrlSearchParamsReturn<TSchema> {
  const { schema, onPopState, writeDefaultsToUrl = false } = options;

  const history = useHistory();
  const location = useLocation();

  // Ref'ler: stale closure önlemi
  const onPopStateRef = useRef(onPopState);
  onPopStateRef.current = onPopState;

  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  // -----------------------------------------------------------------------
  // URL'den parse
  // -----------------------------------------------------------------------
  const params = useMemo(() => parseParams(schema, location.search), [schema, location.search]);

  // -----------------------------------------------------------------------
  // İlk render'da eksik default'ları URL'e yaz (replace — history kirlenmez)
  // -----------------------------------------------------------------------
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!writeDefaultsToUrl || initializedRef.current) return;
    initializedRef.current = true;

    const searchParams = new URLSearchParams(location.search);
    let needsUpdate = false;

    for (const key of Object.keys(schema)) {
      if (!searchParams.has(key)) {
        const def = schema[key];
        const serialized = def.serialize(def.defaultValue);
        if (serialized !== null && serialized !== '') {
          searchParams.set(key, serialized);
          needsUpdate = true;
        }
      }
    }

    if (needsUpdate) {
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }
    // İlk mount'ta bir kez çalışmalı
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------------
  // Browser back/forward (POP) navigasyonu dinle
  // -----------------------------------------------------------------------
  useEffect(() => {
    const unlisten = history.listen((newLocation, action) => {
      if (action === 'POP' && onPopStateRef.current) {
        const popParams = parseParams(schemaRef.current, newLocation.search);
        onPopStateRef.current(popParams);
      }
    });
    return unlisten;
  }, [history]);

  // -----------------------------------------------------------------------
  // URL güncelleme yardımcısı
  // -----------------------------------------------------------------------
  const pushUrl = useCallback(
    (newValues: InferUrlParams<TSchema>) => {
      const search = buildSearchString(schemaRef.current, newValues, location.search);

      history.push({
        pathname: location.pathname,
        search,
      });
    },
    [history, location.pathname, location.search],
  );

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  const setParam = useCallback(
    <K extends keyof TSchema & string>(key: K, value: TSchema[K] extends UrlParamDefinition<infer V> ? V : never) => {
      const current = parseParams(schemaRef.current, location.search);
      pushUrl({ ...current, [key]: value } as InferUrlParams<TSchema>);
    },
    [pushUrl, location.search],
  );

  const setParams = useCallback(
    (partial: Partial<InferUrlParams<TSchema>>) => {
      const current = parseParams(schemaRef.current, location.search);
      pushUrl({ ...current, ...partial } as InferUrlParams<TSchema>);
    },
    [pushUrl, location.search],
  );

  const resetParams = useCallback(() => {
    const defaults = {} as Record<string, any>;
    for (const key of Object.keys(schemaRef.current)) {
      defaults[key] = schemaRef.current[key].defaultValue;
    }
    pushUrl(defaults as InferUrlParams<TSchema>);
  }, [pushUrl]);

  return { params, setParam, setParams, resetParams };
}

// ---------------------------------------------------------------------------
// Built-in UrlParamDefinition fabrikaları (yardımcı fonksiyonlar)
// ---------------------------------------------------------------------------

/**
 * String tipinde URL parametresi tanımı oluşturur.
 * Boş string URL'den kaldırılır.
 *
 * @param defaultValue - Varsayılan değer (default: '')
 *
 * @example
 * ```ts
 * schema: { search: urlString() }
 * // URL: ?search=laptop → params.search = 'laptop'
 * // URL: (yok)          → params.search = ''
 * ```
 */
export function urlString(defaultValue = ''): UrlParamDefinition<string> {
  return {
    defaultValue,
    serialize: (v) => (v ? v : null),
    deserialize: (r) => r ?? defaultValue,
  };
}

/**
 * Number tipinde URL parametresi tanımı oluşturur.
 * Geçersiz veya `min` altındaki değerler `defaultValue`'ya döner.
 *
 * @param defaultValue - Varsayılan değer
 * @param min - Minimum kabul edilebilir değer (default: 1)
 *
 * @example
 * ```ts
 * schema: { page: urlNumber(1), limit: urlNumber(10) }
 * // URL: ?page=3&limit=20 → params.page = 3, params.limit = 20
 * // URL: ?page=abc        → params.page = 1 (defaultValue)
 * ```
 */
export function urlNumber(defaultValue: number, min = 1): UrlParamDefinition<number> {
  return {
    defaultValue,
    serialize: (v) => String(v),
    deserialize: (r) => {
      if (r === null) return defaultValue;
      const n = parseInt(r, 10);
      return isNaN(n) || n < min ? defaultValue : n;
    },
  };
}

/**
 * Virgülle ayrılmış string array URL parametresi tanımı oluşturur.
 * Boş array URL'den kaldırılır.
 *
 * @example
 * ```ts
 * schema: { category: urlCommaSeparated() }
 * // URL: ?category=books,electronics → params.category = ['books', 'electronics']
 * // URL: (yok)                       → params.category = []
 * ```
 */
export function urlCommaSeparated(): UrlParamDefinition<string[]> {
  return {
    defaultValue: [],
    serialize: (v) => (v.length > 0 ? v.join(',') : null),
    deserialize: (r) => (r ? r.split(',').filter(Boolean) : []),
  };
}

/**
 * Boolean tipinde URL parametresi tanımı oluşturur.
 * `false` değeri URL'den kaldırılır, `true` değeri `'1'` olarak saklanır.
 *
 * @param defaultValue - Varsayılan değer (default: false)
 *
 * @example
 * ```ts
 * schema: { hasComment: urlBoolean() }
 * // URL: ?hasComment=1 → params.hasComment = true
 * // URL: (yok)         → params.hasComment = false
 * ```
 */
export function urlBoolean(defaultValue = false): UrlParamDefinition<boolean> {
  return {
    defaultValue,
    serialize: (v) => (v ? '1' : null),
    deserialize: (r) => r === '1' || r === 'true',
  };
}
